import jQuery from 'jquery';
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { checkIfTablet } from './utils.js';
import HeaderWrapper from './redesign/components/Header/HeaderWrapper';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../api/reactQueryClient.ts';
import { renderLazyComponent } from './hooks/renderLazyComponent.jsx';
import Footer from './redesign/components/Footer/Footer.tsx';
import ProductCatalogueHeaderWrapper from './redesign/components/Header/ProductCatalogueHeaderWrapper.tsx';
import ShipsViewerRoot from './components/ships-viewer/ShipsViewerRoot.jsx';
import SearchAutocompleteRoot from './components/autocomplete/SearchAutocompleteRoot.jsx';

const Search = lazy(() => import('./components/search/Search.jsx'));
const ProductsListingRoot = lazy(() => import('./components/products-listing/ProductsListingRoot.jsx'));
const OrderingInfoWithVariantsRoot = lazy(() => import('./components/OrderingInfoWithVariants/OrderingInfoWithVariants.jsx'));
const SDSRoot = lazy(() => import('./components/sds/SDSRoot.jsx'));
const Teaser = lazy(() => import('./components/common/Teaser/Teaser.jsx'));
const OurProducts = lazy(() => import('./components/imo2020/OurProducts/OurProducts.jsx'));
const AuthoringInfo = lazy(() => import('./components/common/AuthoringInfo.jsx'));
const Map = lazy(() => import('./components/map/Map.jsx'));
const ContactExpert = lazy(() => import('./components/ContactExpert/ContactExpert.jsx'));
const PostFeed = lazy(() => import('./components/PostFeed/PostFeed.jsx'));
const Carousel = lazy(() => import('./components/Carousel/Carousel.jsx'));
const Tabs = lazy(() => import('./components/Tabs/Tabs.jsx'));
const AreaContactsRoot = lazy(() => import('./components/AreaContacts/AreaContactsRoot.jsx'));
const InteractiveList = lazy(() => import('./components/InteractiveList/InteractiveList.jsx'));
const CareersPage = lazy(() => import('./pages/CareersPage/CareersPage.jsx'));
const AuthorisedDistributors = lazy(() => import('./components/AuthorisedDistributors/AuthorisedDistributors.jsx'));

const productsListingRootId = 'productsListingRoot',
  shipsViewerRootId = 'shipsViewerRoot',
  reactAutocompleteId = 'reactAutocomplete',
  reactAutocompleteGlobalId = 'reactAutocompleteGlobal',
  orderingInfoWithVariantsRootId = 'orderingInfoWithVariantsRoot',
  sdsRootId = 'sdsRoot',
  teaserClassName = 'teaser-container',
  ourProductsRootId = 'ourProductsRootId',
  mapRootClassName = 'mapRoot',
  searchClassName = 'search-component-root',
  contactExpertId = 'contactExpertId',
  postFeedId = 'maritimePostFeed',
  carouselClassName = 'carousel-root',
  tabsClassName = 'tabs-root',
  areaContactsClassName = 'area-contacts-root',
  interactiveClassName = 'interactive-list',
  careersPageId = 'careers-page',
  authorisedDistributorsId = 'distributors-root',
  headerRoot = 'header-root',
  catalogueHeaderRoot = 'catalogue-header-root',
  footerContainer = 'footer-container';

// All code below accesses the DOM and must not run in the V8 SSR engine.
// document.__isSSR is set by SsrPolyfill.js when running inside the V8 engine pool.
if (typeof document !== 'undefined' && !document.__isSSR) {

// Legacy jQuery plugins (copy contact, add shortcut, nav, tabs, etc.) lived in js/index.js
// before the SSR bundle entry moved to main.tsx. Re-attach them for Razor-rendered pages.
window.jQuery = jQuery;
window.$ = jQuery;
import('../js/index.js');

import('../js/accordion.js');

const footerElement = document.getElementById(footerContainer);
if (footerElement) {
  const currentPageId = footerElement.getAttribute('data-current-page-id') || '';

  ReactDOM.render (
      <Footer
        currentPageId={parseInt(currentPageId)}
      />,
    footerElement
  );
}

if (document.getElementById(headerRoot) !== null) {
  const containerElement = document.getElementById(headerRoot);
  const frontPageTitle = containerElement.getAttribute('data-front-page-title');
  const frontPageUrl = containerElement.getAttribute('data-front-page-url');
  const frontPageId = containerElement.getAttribute('data-front-page-id');
  const currentPageId = containerElement.getAttribute('data-current-page-id');

  ReactDOM.render(
      <QueryClientProvider client={queryClient}>
        <HeaderWrapper
          frontPageTitle={frontPageTitle}
          frontPageUrl={frontPageUrl}
          frontPageId={frontPageId}
          currentPageId={currentPageId}
        />
      </QueryClientProvider>,
    containerElement
  );
}

if (document.getElementById(catalogueHeaderRoot) !== null) {
  const containerElement = document.getElementById(catalogueHeaderRoot);
  const frontPageTitle = containerElement.getAttribute('data-front-page-title');
  const frontPageUrl = containerElement.getAttribute('data-front-page-url');
  const frontPageId = containerElement.getAttribute('data-front-page-id');
  const currentPageId = containerElement.getAttribute('data-current-page-id');

  ReactDOM.render(
    <QueryClientProvider client={queryClient}>
      <ProductCatalogueHeaderWrapper
        frontPageTitle={frontPageTitle}
        frontPageUrl={frontPageUrl}
        frontPageId={frontPageId}
        currentPageId={currentPageId}
      />
    </QueryClientProvider>,
    containerElement
  );
}

// This component is used both for category results and search results to list and filter products
if (document.getElementById(productsListingRootId) !== null) {
  const containerElement = document.getElementById(productsListingRootId),
    categoryId = containerElement.getAttribute('data-categoryid') || '',
    searchTerm = containerElement.getAttribute('data-term') || '';
  let rootUrl;

  if (containerElement.hasAttribute('data-term')) {
    rootUrl = '/api/product/search';
  } else {
    rootUrl = '/api/product/getByCategory';
  }

  renderLazyComponent(
    <ProductsListingRoot
      categoryId={categoryId}
      searchTerm={searchTerm}
      rootUrl={rootUrl}
    />,
    containerElement
  );
}

if (checkIfTablet() && document.getElementById(shipsViewerRootId) !== null) {
  const containerElement = document.getElementById(shipsViewerRootId),
    dataContainer = document.getElementById(shipsViewerRootId + '-data'),
    shipsAssetsHtmlCollection = dataContainer.getElementsByClassName(
      'ships-viewer__ship-assets-data'
    ),
    shipsAssetsRaw = Array.prototype.slice.call(shipsAssetsHtmlCollection);

    ReactDOM.render(
      <ShipsViewerRoot shipsAssetsRaw={shipsAssetsRaw} />,
      containerElement
    );
}

if (document.getElementById(reactAutocompleteId) !== null) {

  renderLazyComponent(
    <SearchAutocompleteRoot

      dataAttributeOwnerId={reactAutocompleteId}
      categorySelectElementId="searchResultCategory"
      minCharacters={3}
    />,
    document.getElementById(reactAutocompleteId)
  );
}

if (document.getElementById(reactAutocompleteGlobalId) !== null) {
  ReactDOM.render(
    <SearchAutocompleteRoot
      dataAttributeOwnerId={reactAutocompleteGlobalId}
      categorySelectElementId="globalCategory"
      minCharacters={3}
    />,
    document.getElementById(reactAutocompleteGlobalId)
  );
}

if (document.getElementById(orderingInfoWithVariantsRootId) !== null) {
  const containerElement = document.getElementById(
    orderingInfoWithVariantsRootId
  );
  const productId = containerElement.getAttribute('data-product-id') || '';
  const tableType = containerElement.getAttribute('ordering-information-table-style') || null;

  const sdmbl = {
    visible: containerElement.getAttribute('data-sdmbl-visible') || false,
    min: containerElement.getAttribute('data-sdmbl-min') || 0,
    max: containerElement.getAttribute('data-sdmbl-max') || 0,
  };

  const getRootUrl = (tableType) => {
    switch (tableType) {
      case "BundlePackage":
        return '/api/BundlePackages/GetByPageId';
      case "BundlePackageGroup":
        return '/api/BundleGroupPackages/GetByPageId';
      default:
        return '/api/ropeorderinginfo/getbypageid';
    }
  }
  const apiRootUrl = getRootUrl(tableType);

  renderLazyComponent(
    <OrderingInfoWithVariantsRoot
      productId={productId}
      apiRootUrl={apiRootUrl}
      sdmbl={sdmbl}
    />,
    containerElement
  );
}

if (document.getElementById(sdsRootId) !== null) {
  const containerElement = document.getElementById(sdsRootId);
  const productId = containerElement.getAttribute('data-product-id') || '';

  renderLazyComponent(<SDSRoot productId={productId} />, containerElement);
}

if (document.getElementById(ourProductsRootId) !== null) {
  const containerElement = document.getElementById(ourProductsRootId);

  renderLazyComponent(<OurProducts />, containerElement);
}

if (document.getElementsByClassName(teaserClassName) !== null) {
  const elements = document.getElementsByClassName(teaserClassName);
  [...elements].forEach(element => {
    const teaserUrl = element.getAttribute('data-teaser-url');
    const teaserText = element.getAttribute('data-teaser-text');
    const imageUrl = element.getAttribute('data-image-url');
    const imageAlt = element.getAttribute('data-image-alt');
    const imageTitle = element.getAttribute('data-image-title');
    const authorName = element.getAttribute('data-author-name');
    const dateMicro = element.getAttribute('data-date-micro');
    const date = element.getAttribute('data-date');
    const videoUrl = element.getAttribute('data-video-local-url');
    const videoEmbed = element.getAttribute('data-video-embed-code');
    const formUrl = element.getAttribute('data-form-url');

    renderLazyComponent(
      <Teaser
        teaserUrl={teaserUrl}
        teaserText={teaserText}
        imageUrl={imageUrl}
        imageAlt={imageAlt}
        imageTitle={imageTitle}
        localVideoUrl={videoUrl}
        videoEmbed={videoEmbed}
        formUrl={formUrl}
      >
        <AuthoringInfo
          authorName={authorName}
          dateMicro={dateMicro}
          date={date}
        />
      </Teaser>,
      element
    );
  });
}

if (document.getElementsByClassName(mapRootClassName) !== null) {
  const mapContainerElements = document.getElementsByClassName(mapRootClassName);
  [...mapContainerElements].forEach(element => {
    const guid = element.getAttribute('data-guid');
    let mapCoordinates = element.getAttribute('data-coordinates');
    if (mapCoordinates) {
      mapCoordinates = JSON.parse(mapCoordinates);
      mapCoordinates = [{ Latitude: mapCoordinates[0], Longitude: mapCoordinates[1], "typeVariable": "office" }];
    }
    const mapSize = element.getAttribute('data-map-size');

    renderLazyComponent(<Map guid={guid} mapSize={mapSize} coordinates={mapCoordinates} />, element);
  });
}

if (document.getElementsByClassName (searchClassName) !== null) {
  const elements = document.getElementsByClassName (searchClassName);
  [...elements].forEach (element => {
    const searchPlaceholder = element.getAttribute ('data-placeholder');
    const searchType = element.getAttribute ('data-search-type');
    const buttonType = element.getAttribute ('data-button-type');
    const useEmptySearch = element.getAttribute ('data-use-empty-search') === 'true';

    renderLazyComponent(
      <Search
        placeholder={searchPlaceholder}
        searchType={searchType}
        buttonType={buttonType}
        useEmptySearch={useEmptySearch}
      />,
      element
    );
  });
}

if (document.getElementById(contactExpertId) !== null) {
  const containerElement = document.getElementById(contactExpertId);
  const formUrl = containerElement.getAttribute('data-form-url');
  renderLazyComponent(<ContactExpert formUrl={formUrl} />, containerElement);
}

if (document.getElementById(postFeedId) !== null) {
  const containerElement = document.getElementById(postFeedId);
  const showFilters = containerElement.getAttribute('data-show-filters') == 'true';
  const categoryId = containerElement.getAttribute('data-category-id');
  const parentCategoryId = containerElement.getAttribute('data-parent-category-id');
  renderLazyComponent(
    <PostFeed showFilters={showFilters}
      categoryId={categoryId}
      parentCategoryId={parentCategoryId}
    />,
    containerElement
  );
}

if (document.getElementsByClassName(carouselClassName) !== null) {
  const elements = document.getElementsByClassName(carouselClassName);
  [...elements].forEach(element => {
    const parent = element.parentNode;
    const itemsContainer = parent.querySelector('.carousel-data');

    renderLazyComponent(
      <Carousel
        items={itemsContainer.querySelectorAll('.teaser-list__carousel-placeholder')}
      />,
      element
    );
  });
}

if (document.getElementsByClassName(tabsClassName) !== null) {
  const elements = document.getElementsByClassName(tabsClassName);
  [...elements].forEach(element => {
    let items = element.getAttribute('data-items');
    if (items) {
      items = JSON.parse(items);
    }

    renderLazyComponent(
      <Tabs items={items}
      />,
      element
    );
  });
}

if (document.getElementsByClassName(areaContactsClassName) !== null) {
  const elements = document.getElementsByClassName(areaContactsClassName);
  [...elements].forEach(element => {
    const bgImage = element.getAttribute('data-image');
    const areas = JSON.parse(element.getAttribute('data-areas'));

    renderLazyComponent(
      <AreaContactsRoot
        bgImage={bgImage}
        areas={areas}
      />,
      element
    );
  });
}

if (document.getElementsByClassName(interactiveClassName) !== null) {
  const elements = document.getElementsByClassName(interactiveClassName);
  [...elements].forEach(el => {
    let listData = el.getAttribute('data-list');
    listData = JSON.parse(listData);

    renderLazyComponent(
      <InteractiveList list={listData} />,
      el
    );
  })
}

if (document.getElementById(careersPageId) !== null) {
  const el = document.getElementById(careersPageId);

  renderLazyComponent(
    <CareersPage />,
    el
  );
}
if (document.getElementById(authorisedDistributorsId) !== null) {
  const containerElement = document.getElementById(
    authorisedDistributorsId
  );

  renderLazyComponent(
    <AuthorisedDistributors />,
    containerElement
  );
}

} // end typeof document !== 'undefined'
